import { getAllClinicsQuery } from '@/api/clinics/clinicQueries';
import type { CreateInvitationRequest } from '@/api/invitations/invitationMutations';
import { createInvitationMutation } from '@/api/invitations/invitationMutations';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { PhoneInput } from '@/components/common/PhoneInput';
import { Select } from '@/components/common/Select';
import { useFormMutation } from '@/hooks/useFormMutation';
import type { InvitePractitionerFormValues } from '@/validation/practitionerValidator';
import { invitePractitionerSchema } from '@/validation/practitionerValidator';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { toFormikValidate } from 'zod-formik-adapter';
import { DashboardTableAccordion } from '../DashboardTableAccordion';

export default function InvitePractitioner() {
  const { data: clinics = [], isLoading: isClinicsLoading } =
    useQuery(getAllClinicsQuery());

  const clinicOptions = clinics.map((clinic) => ({
    label: clinic.name,
    value: String(clinic.id),
  }));

  const formik = useFormik<InvitePractitionerFormValues>({
    initialValues: {
      name: '',
      surname: '',
      email: '',
      clinic: '',
      phone: '',
    },
    validate: toFormikValidate(invitePractitionerSchema),
    onSubmit: async (values) => {
      const payload: CreateInvitationRequest = {
        email: values.email,
        name: values.name,
        surname: values.surname,
        role: 'Doctor',
        clinic_id: values.clinic,
        phone: values.phone,
      };
      await createInvitation.mutateAsync(payload);
      formik.resetForm();
    },
  });

  const createInvitation = useFormMutation({
    ...createInvitationMutation(),
    formik,
  });

  return (
    <DashboardTableAccordion
      value="Invite practitioner"
      title="Invite practitioner"
      data={[{ id: 1 }]}
      isLoading={isClinicsLoading}
      tableWrapper="card"
      customTable={
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 pt-2.5 md:flex-row">
            <Input
              id="name"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.name}
              touched={formik.touched.name}
            />
            <Input
              id="surname"
              name="surname"
              label="Surname"
              value={formik.values.surname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors.surname}
              touched={formik.touched.surname}
            />
          </div>

          <Input
            id="email"
            name="email"
            type="email"
            label="Email Address"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.email}
            touched={formik.touched.email}
          />
          <Select
            id="clinic"
            name="clinic"
            label="Clinic"
            options={clinicOptions}
            value={formik.values.clinic}
            onChange={(value) => formik.setFieldValue('clinic', value)}
            onBlur={() => formik.setFieldTouched('clinic', true)}
            error={formik.errors.clinic}
            touched={formik.touched.clinic}
          />

          <PhoneInput
            id="phone"
            name="phone"
            label="Phone Number"
            value={formik.values.phone}
            onChange={(value) => formik.setFieldValue('phone', value)}
            onBlur={() => formik.setFieldTouched('phone', true)}
            error={formik.errors.phone}
            touched={formik.touched.phone}
          />
        </form>
      }
      footerActions={
        <Button
          variant="primary"
          size="md"
          disabled={createInvitation.isPending}
          onClick={() => formik.submitForm()}
        >
          {createInvitation.isPending ? 'Sending...' : 'Send Invitation'}
        </Button>
      }
    />
  );
}
